import logger from "../utils/logger";
import { useState, useEffect, useCallback, useRef } from 'react';
import { getPropertyPhotoUrl, getPropertyDocumentUrl } from '../services/supabase/propertyStorage';

/**
 * Hook do pobierania podpisanego URL zdjęcia nieruchomości
 */
export function usePropertyPhotoUrl(path, expiresIn = 3600) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  const fetchUrl = useCallback(async () => {
    if (!path) {
      setUrl(null);
      setLoading(false);
      return;
    }

    logger.log('[usePropertyPhotoUrl] Starting fetch...', { path });
    const fetchStartTime = Date.now();
    let timeoutId;

    setLoading(true);
    setError(null);

    try {
      // Safety timeout: force loading to false after 8 seconds
      timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          console.error('[usePropertyPhotoUrl] Fetch timeout after 8s - forcing loading to false');
          setLoading(false);
          setError('Photo load timeout');
        }
      }, 8000);

      const signedUrl = await getPropertyPhotoUrl(path, expiresIn);

      if (!isMountedRef.current) {
        clearTimeout(timeoutId);
        return;
      }

      clearTimeout(timeoutId);
      logger.log(`[usePropertyPhotoUrl] Fetch completed in ${Date.now() - fetchStartTime}ms`);
      setUrl(signedUrl);
    } catch (err) {
      clearTimeout(timeoutId);
      if (isMountedRef.current) {
        console.error('[usePropertyPhotoUrl] Error fetching property photo URL:', err);
        setError(err.message);
        setUrl(null);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [path, expiresIn]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchUrl();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchUrl]);

  return {
    url,
    loading,
    error,
    refresh: fetchUrl,
  };
}

/**
 * Hook do pobierania podpisanego URL dokumentu nieruchomości
 */
export function usePropertyDocumentUrl(path, expiresIn = 3600) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUrl = useCallback(async () => {
    if (!path) {
      setUrl(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const signedUrl = await getPropertyDocumentUrl(path, expiresIn);
      setUrl(signedUrl);
    } catch (err) {
      console.error('Error fetching property document URL:', err);
      setError(err.message);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  }, [path, expiresIn]);

  useEffect(() => {
    fetchUrl();
  }, [fetchUrl]);

  return {
    url,
    loading,
    error,
    refresh: fetchUrl,
  };
}
